import { Service, TimeGranularity, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { useModelService } from 'services/model/model.service';
import { useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { MetricsChart } from 'components/MetricsChart';
import { PageContainer, Tile, TileContent } from 'components/Layout/PageContainer';
import { DataEntry } from 'models/data';
import { PostingDateReportInfoContent } from 'pages/insights-pro/performance-tab/reports/PostingDateReportInfoContent';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { format, parse } from 'date-fns';
import { areAllValuesZero } from 'utils/areAllFieldsZero';
import { EmptyState } from 'components/EmptyState';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { ExpandableRow } from 'components/table/ExpandableRow';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { createExpanderCell } from 'components/table/DataTableUtil';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { ComparisonCell, useDataEntriesInTables } from 'components/table/useDataEntriesInTables';

interface PostingDateRow extends ExpandableRow<PostingDateRow> {
  label: string;
  sortDate: string;
  ordGmvVal: ComparisonCell;
  ordNum: ComparisonCell;
  ordQty: ComparisonCell;
  retQty: ComparisonCell;
  retGmvVal: ComparisonCell;
  netSlsGmvVal: ComparisonCell;
  netSlsGmvValExt: ComparisonCell;
}

const MEASURE_KEYS: (keyof Omit<PostingDateRow, 'label' | 'sortDate' | 'subRows'>)[] = [
  'ordGmvVal',
  'ordNum',
  'ordQty',
  'retQty',
  'retGmvVal',
  'netSlsGmvVal',
  'netSlsGmvValExt',
];

const columnHelper = createColumnHelper<PostingDateRow>();

const getDayString = (dateTime: string) => {
  const parsedDate = parse(dateTime, 'yyyy-MM-dd', new Date());
  return format(parsedDate, 'dd/MM/yyyy');
};

const getMonthString = (dateTime: string, intl: ReturnType<typeof useIntl>) => {
  const parsedDate = parse(`${dateTime}-01`, 'yyyy-MM-dd', new Date());
  return intl.formatDate(parsedDate, { year: 'numeric', month: 'long' });
};

export function PostingDatePerformanceReport() {
  const { setContent } = useInfoDrawer();
  const labelContext = useDatePickerLabels();

  const { fetchData } = useProDataService(Service.Performance);

  const requestData = useDataRequest();
  const refinedRequestData = useMemo(() => {
    if (requestData.timeFilter?.granularity === TimeGranularity.WEEK) {
      return { ...requestData, timeFilter: { ...requestData.timeFilter, granularity: TimeGranularity.MONTH } };
    }
    return requestData;
  }, [requestData]);

  const granularity = useMemo(() => refinedRequestData.timeFilter?.granularity, [refinedRequestData]);

  const { getMeasuresByMetrics, getMeasures, getAttribute } = useModelService();

  const [ordGmvVal, ordNum, ordQty] = getMeasuresByMetrics(['ordGmvVal', 'ordNum', 'ordQty'], 'ED');

  const [retQty, retGmvVal, netSlsGmvVal, netSlsGmvValExt] = getMeasures([
    'retQty',
    'retGmvVal',
    'netSlsGmvVal',
    'netSlsGmvValExt',
  ]);

  const measures = [ordGmvVal, ordNum, ordQty, retQty, retGmvVal, netSlsGmvVal, netSlsGmvValExt];

  const [chartOrdGmvVal, chartRetGmvVal, chartNetSlsGmvVal] = getMeasures(['ordGmvVal', 'retGmvVal', 'netSlsGmvVal']);
  const chartMeasures = [chartOrdGmvVal, chartRetGmvVal, chartNetSlsGmvVal, netSlsGmvValExt];

  const intl = useIntl();

  const [rows, setRows] = useState<PostingDateRow[]>([]);

  useEffect(() => {
    const infoContent = {
      titleKey: 'insights.performance.reports.posting_date',
      subtitleKey: 'info.content.all_dates_utc',
      content: <PostingDateReportInfoContent measures={measures} />,
    };

    setContent(infoContent);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const reportState = useDataServiceQueryResult(
    refinedRequestData,
    baseData =>
      fetchData('reports/posting-date', {
        ...baseData,
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );
      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-performance-posting-date-report', granularity ?? 'none']
  );

  const lineRenderer = useMemo(() => {
    if (reportState.data) {
      return new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: chartMeasures,
          request: requestData,
          intl,
          response: reportState.data.response,
          labelContext,
        })
      );
    }

    return null;
  }, [reportState.data, intl]);

  useEffect(() => {
    if (!reportState.data) return;
    const { timeSeries } = reportState.data.response.sets;

    const mapEntryToRow = (entry: DataEntry): PostingDateRow => ({
      label: entry.attributes.timeDate
        ? getDayString(entry.attributes.timeDate)
        : getMonthString(entry.attributes.timeYearMonth, intl),
      sortDate: entry.attributes.timeDate ?? entry.attributes.timeYearMonth,
      ...(Object.fromEntries(
        MEASURE_KEYS.map((key, index) => [
          key,
          {
            value: measures[index].getValue(entry.values),
            compareValue: measures[index].getValue(entry.compareValues),
          },
        ])
      ) as Omit<PostingDateRow, 'label' | 'sortDate' | 'subRows'>),
      subRows: [],
    });

    if (granularity === TimeGranularity.DAY) {
      setRows(timeSeries.map(entry => mapEntryToRow(entry)));
      return;
    }

    const fetchAllDayData = async () => {
      const monthRows = await Promise.all(
        timeSeries.map(async monthEntry => {
          const dayResponse = await fetchData('reports/posting-date', {
            ...refinedRequestData,
            timeFilter: {
              fromDate: monthEntry.dateRanges.timeYearMonth.fromDate,
              toDate: monthEntry.dateRanges.timeYearMonth.toDate,
              granularity: TimeGranularity.DAY,
            },
          });

          const dayRows = (dayResponse.data?.sets.timeSeries ?? [])
            .filter(day => {
              const entryDate = new Date(day.attributes.timeDate);
              return (
                entryDate >= new Date(requestData.timeFilter?.fromDate ?? '') &&
                entryDate <= new Date(requestData.timeFilter?.toDate ?? '')
              );
            })
            .map(day => mapEntryToRow(day));

          return {
            ...mapEntryToRow(monthEntry),
            subRows: dayRows,
          };
        })
      );
      setRows(monthRows);
    };

    fetchAllDayData();
  }, [reportState.data, intl.locale]);

  const isComparisonActive = reportState.data?.isComparisonActive ?? false;

  const { createMeasureColumn } = useDataEntriesInTables();
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('label', {
          header: () => intl.formatMessage({ id: 'insights.order.posting-date', defaultMessage: 'Posting Date' }),
          cell: context =>
            createExpanderCell(context, <span className="text-inherit">{context.getValue()}</span>, true),
          sortingFn: (rowA, rowB) => rowA.original.sortDate.localeCompare(rowB.original.sortDate),
          meta: {
            sortTitleId: 'insights.order.posting-date',
          },
        }),
        ...measures.map(measure => createMeasureColumn(measure, { ignoreComparison: !isComparisonActive })),
      ] as ColumnDef<PostingDateRow>[],
    [measures, intl, isComparisonActive]
  );

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Performance,
      measuresGroup: 'posting-date',
      tableName: 'Posting Date Report',
      isProReport: true,
    },
    skippedColumns: ['sortDate'],
  };

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const reportData = reportState.data;

  return (
    <PageContainer>
      <Tile
        titleId="insights.performance.reports.posting_date"
        data-testid="posting-date-tile"
        className="text-lg p-4"
      />
      <Tile
        titleId="orders.title.gmv"
        additionalTitle={intl.formatMessage({
          id: 'general.title.over_time',
        })}
      >
        <TileContent className="flex flex-col items-center gap-y-4">
          <MetricsChart renderer={lineRenderer} height="h-[256px]" testId="posting-date-chart" />
        </TileContent>
      </Tile>

      {reportData?.response?.sets?.timeSeries?.every(item => areAllValuesZero(item.values)) ? (
        <EmptyState />
      ) : (
        <DataTableContextProvider>
          <DataTable
            titleId="insights.performance.reports.posting_date.description"
            rows={rows}
            columns={columns}
            maxPageSize={10}
            defaultExpandedState={{}}
            defaultSortingState={[{ id: 'label', desc: true }]}
            exportConfigOptions={exportConfigOptions}
            subtitleBySortedColumn={true}
            isError={reportState.isError}
            errorMessage={reportState.error}
            testId="posting-date-table"
          />
        </DataTableContextProvider>
      )}
    </PageContainer>
  );
}
